// lib/dataCache.ts as a hook: the cached value renders first and the fresh
// one replaces it when the policy asks for a refresh, so a list the user saw
// yesterday is on screen before the edge has answered, and stays there when
// it cannot answer at all.
import { useCallback, useEffect, useRef, useState } from "react";
import { isFresh, readEntry, writeEntry, type CachePolicy, type Guard } from "./dataCache";

export interface Cached<T> {
  data: T | undefined;
  /** True when `data` is older than its policy allows and no fresh copy is coming: offline, disabled, or the refresh failed. */
  stale: boolean;
  /** The last loader failure, when the cache had nothing or could not be refreshed. */
  error: unknown;
  /** True while a fetch that will change `data` is in flight. */
  loading: boolean;
  /** Reads the cache again and refreshes per the policy. */
  refresh: () => void;
}

interface State<T> extends Omit<Cached<T>, "refresh"> {
  /** The key this state was produced for, so a key change never shows the previous key's data for a frame. */
  forKey: string | undefined;
}

const idle: State<never> = {
  forKey: undefined,
  data: undefined,
  stale: false,
  error: undefined,
  loading: false,
};

/**
 * With `enabled` false (the edge is known to be down) only the cache is read,
 * so a saved copy still shows offline. The loader and guard are read through
 * a ref, so an inline arrow never restarts the fetch.
 */
export function useCached<T>(
  key: string | undefined,
  policy: CachePolicy,
  loader: () => Promise<T>,
  is: Guard<T>,
  enabled = true,
): Cached<T> {
  const latest = useRef({ loader, is });
  useEffect(() => {
    latest.current = { loader, is };
  });
  const [state, setState] = useState<State<T>>(idle);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (key === undefined) {
      setState(idle);
      return undefined;
    }
    let cancelled = false;
    setState((s) =>
      s.forKey === key ? { ...s, loading: true } : { ...idle, forKey: key, loading: true },
    );
    (async () => {
      const entry = await readEntry(key, policy, latest.current.is);
      if (cancelled) return;
      const fresh = entry !== undefined && isFresh(entry, policy);
      const refreshing = !fresh && enabled;
      if (entry !== undefined) {
        setState({
          forKey: key,
          data: entry.data,
          stale: !fresh && !enabled,
          error: undefined,
          loading: refreshing,
        });
      } else if (!refreshing) {
        setState({ ...idle, forKey: key });
      }
      if (!refreshing) return;
      try {
        const data = await latest.current.loader();
        await writeEntry(key, policy, data);
        if (!cancelled)
          setState({ forKey: key, data, stale: false, error: undefined, loading: false });
      } catch (error: unknown) {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            forKey: key,
            stale: s.data !== undefined,
            error,
            loading: false,
          }));
        }
      }
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [key, policy, enabled, generation]);

  const refresh = useCallback(() => {
    setGeneration((g) => g + 1);
  }, []);

  // Until the effect has run for this key, the hook is loading it (or idle
  // without a key); state for another key is never handed out.
  const current: State<T> =
    state.forKey === key ? state : { ...idle, forKey: key, loading: key !== undefined };
  return {
    data: current.data,
    stale: current.stale,
    error: current.error,
    loading: current.loading,
    refresh,
  };
}
